// Decorative examples only: every feature stays visible in the page flow.
(() => {
  if (!customElements.get('solvia-glance-motion')) {
    customElements.define('solvia-glance-motion', class extends HTMLElement {
      connectedCallback() {
        if (this.events) return;
        this.events = new AbortController();
        const button = this.querySelector('.solvia-glance__pause');
        if (!button) return;
        button.hidden = false;
        button.addEventListener('click', () => {
          const paused = this.dataset.paused !== 'true';
          this.dataset.paused = String(paused);
          button.setAttribute('aria-pressed', String(paused));
          button.textContent = paused ? 'Resume animations' : 'Pause animations';
          this.dispatchEvent(new Event('glance-motion-change'));
        }, { signal: this.events.signal });
      }
      disconnectedCallback() { this.events?.abort(); this.events = null; }
    });
  }
  if (customElements.get('solvia-feature-art')) return;

  customElements.define('solvia-feature-art', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const { signal } = this.events;
      this.examples = [...this.querySelectorAll('[data-feature-example], [data-camera-frame]')];
      this.index = 0;
      this.running = false;
      this.visible = false;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.isDialogue = this.dataset.featureKind === 'assistant';
      this.isGlanceCamera = this.dataset.featureKind === 'glance-camera';
      this.isCamera = this.dataset.featureKind === 'camera' || this.isGlanceCamera;
      this.isTranslation = this.dataset.featureKind === 'translation';
      if (this.isTranslation) this.dataset.translationStep = 'complete';
      this.hold = this.isGlanceCamera ? 2000 : this.dataset.featureKind === 'glance-dialogue' ? 4000 : this.isCamera ? 4800 : this.isDialogue ? 6000 : 5200;
      this.glance = this.closest('solvia-glance-motion');
      this.glance?.addEventListener('glance-motion-change', () => this.sync(), { signal });
      this.dataset.motionReady = '';
      this.settle();

      document.addEventListener('visibilitychange', () => this.sync(), { signal });
      this.reduced.addEventListener('change', () => this.sync(), { signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.sync();
        }, { threshold: 0 });
        this.observer.observe(this);
      } else {
        this.visible = true;
      }
      this.sync();
    }

    settle() {
      this.examples.forEach((example, index) => {
        example.classList.toggle('is-active', index === this.index);
        example.classList.remove('is-thinking');
      });
    }

    sync() {
      const running = this.visible && !document.hidden && !this.reduced.matches && !window.Shopify?.designMode && this.glance?.dataset.paused !== 'true';
      if (running === this.running) return;
      this.running = running;
      this.dataset.running = String(running);
      clearTimeout(this.timer);
      this.settle();
      if (this.isTranslation) {
        if (running && this.examples.length) this.playTranslation();
        else this.dataset.translationStep = 'complete';
      } else if (running && this.examples.length > 1) {
        this.later(() => this.next(), this.hold);
      }
    }

    playTranslation() {
      this.dataset.translationStep = 'listen';
      this.later(() => {
        this.dataset.translationStep = 'translate';
        this.later(() => {
          this.dataset.translationStep = 'speak';
          this.later(() => this.next(), 3800);
        }, 2400);
      }, 2200);
    }

    later(callback, delay) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.timer = null;
        if (this.isConnected && this.running) callback();
      }, delay);
    }

    next() {
      if (this.isCamera) {
        this.index = (this.index + 1) % this.examples.length;
        this.settle();
        this.later(() => this.next(), this.hold);
        return;
      }
      // Fade the complete question/answer pair out together. A new question
      // never appears alongside the previous answer, even during a transition.
      this.examples[this.index].classList.remove('is-active');
      this.later(() => {
        this.index = (this.index + 1) % this.examples.length;
        const next = this.examples[this.index];
        next.classList.toggle('is-thinking', this.isDialogue);
        next.classList.add('is-active');
        if (this.isTranslation) {
          this.playTranslation();
        } else if (this.isDialogue) {
          this.later(() => {
            next.classList.remove('is-thinking');
            this.later(() => this.next(), this.hold);
          }, 700);
        } else {
          this.later(() => this.next(), this.hold);
        }
      }, 280);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.running = false;
      this.dataset.running = 'false';
      if (this.isTranslation) this.dataset.translationStep = 'complete';
      this.settle();
      this.observer?.disconnect();
      this.events?.abort();
      this.events = null;
    }
  });
})();
